import { createSelector } from '@reduxjs/toolkit';
import type { ILabel } from '@lt/api/dist/methods/packages/types';
import {
  getAlternativeTours,
  getAltTourById,
} from '../../CheckoutRoutes/PackageCheckout/store/actualize-alternatives';
import {
  aviaSupplierCodeSelector as origPackageAviaSupplierCodeSelector,
  getOperatorId,
  hotelSupplierCodeSelector as origPackageHotelSupllierCodeSelector,
  getHotelLabels,
  getTourLabels,
  getTransfer,
  getIsPrivateTransfer,
  getMedicalInsurance,
  getTourId,
  getNetPrice,
} from './package';
import { CHECKOUT_LAYOUT } from '../constants/package';
import { getDestinationAirports } from './hotel';
import { countTotalExtrasPrice } from '../../utils/orderUtils';
import type { ExtraItem } from '../../types/extras';
import { getOriginalPackageExtras } from './services';
import { parseBundleId } from '../../utils/parseBundleId';

/**
 * Селекторы, объединяющие селекторы из основного пакета и из альтернативных
 */

const altTourSelector = (state, tourId?: string) =>
  getAltTourById(state, tourId || '');

export const aviaSupplierCodeSelector = createSelector(
  [altTourSelector, origPackageAviaSupplierCodeSelector],
  (altTour, origPackageAviaSupplierCode) => {
    if (altTour) {
      return parseBundleId(altTour.misc_data?.bundle_id).avia;
    }
    return origPackageAviaSupplierCode;
  },
);

export const hotelSupplierCodeSelector = createSelector(
  [altTourSelector, origPackageHotelSupllierCodeSelector],
  (altTour, origPackageHotelSupplierCode) => {
    if (altTour) {
      return parseBundleId(altTour.misc_data?.bundle_id).hotel;
    }
    return origPackageHotelSupplierCode;
  },
);

export const operatorIdSelector = createSelector(
  [altTourSelector, getOperatorId],
  (altTour, operatorId: number): number | undefined => {
    if (altTour) {
      return altTour.operator_info.id;
    }
    return operatorId;
  },
);

export const hotelLabelsSelector = createSelector(
  [altTourSelector, getHotelLabels],
  (altTour, origPackageHotelLabels): ILabel[] => {
    if (altTour) {
      return altTour.labels.filter(
        (label) => label.checkout_layout === CHECKOUT_LAYOUT.HOTEL,
      );
    }
    return origPackageHotelLabels;
  },
);

export const tourLabelsSelector = createSelector(
  [altTourSelector, getTourLabels],
  (altTour, origPackageTourLabels): ILabel[] => {
    if (altTour) {
      return altTour.labels.filter(
        (label) => label.checkout_layout === CHECKOUT_LAYOUT.TOUR,
      );
    }
    return origPackageTourLabels;
  },
);

export const transferMedInsuranceSelector = createSelector(
  [
    getTransfer,
    getIsPrivateTransfer,
    getMedicalInsurance,
    altTourSelector,
    getDestinationAirports,
  ],
  (origTransfer, isOrigTransferPrivate, insurance, altTour, airports) => {
    if (altTour) {
      return {
        transfer: altTour.transfer,
        transferPrivate: altTour.misc_data.transfer_type === 'private',
        airports,
        insurance: altTour.medical_insurance,
      };
    }
    return {
      transfer: origTransfer,
      transferPrivate: isOrigTransferPrivate,
      airports,
      insurance,
    };
  },
);

/**
 * Возвращает объект, где ключ - tour_id,
 * а значение - цена пакета + цена обязательных экстрасов
 */
export const packagePriceObjectSelector = createSelector(
  [getAlternativeTours, getTourId, getNetPrice, getOriginalPackageExtras],
  (altTours, originalTourId, originalPrice, originalExtras) => {
    const result: Record<string, number> = {};

    altTours.forEach((altTour) => {
      result[altTour.tour_id] =
        Number(altTour.price) +
        countTotalExtrasPrice(
          altTour.extras.filter((extra) => extra.mandatory) as ExtraItem[],
        );
    });

    result[originalTourId] =
      originalPrice +
      countTotalExtrasPrice(originalExtras.filter((extra) => extra.mandatory));

    return result;
  },
);
